import { useCallback, useEffect, useMemo, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import UtilityBar from "../components/layout/UtilityBar";
import Footer from "../components/layout/Footer";
import ProgressBar from "../components/layout/ProgressBar";
import OutboundCallModal from "../components/modals/OutboundCallModal";
import Toast from "../components/common/Toast";
import useToast from "../hooks/useToast";
import useSidebarState from "../hooks/useSidebarState";
import Header from "./Header";
import Sidebar from "./Sidebar";
import { ShellContext } from "./useShellContext";
import { formatToday } from "../utils/formatters";
import { startOutboundCall } from "../services/outboundApi";
import { normalizeApiError } from "../utils/errors";

const defaultPageControls = {
  onRefresh: null,
  refreshEvery: "Manual",
  lastUpdated: "--",
  isRefreshing: false,
};

export default function AppShell() {
  const location = useLocation();
  const sidebar = useSidebarState();
  const { toasts, showToast, removeToast } = useToast();
  const [pageControls, setPageControls] = useState(defaultPageControls);
  const [outboundBusy, setOutboundBusy] = useState(false);
  const [progressWidth, setProgressWidth] = useState(0);
  const [progressActive, setProgressActive] = useState(false);
  const [outboundModal, setOutboundModal] = useState({
    open: false,
    phone: "",
    name: "",
    reason: "",
    summary: "",
  });

  const {
    isMobile,
    isCollapsed,
    isMobileOpen,
    sidebarWidth,
    toggle,
    closeMobile,
  } = sidebar;

  const showProgress = useCallback(() => {
    setProgressActive(true);
    setProgressWidth(28);
    window.setTimeout(() => setProgressWidth(72), 120);
  }, []);

  const hideProgress = useCallback(() => {
    setProgressWidth(100);
    window.setTimeout(() => {
      setProgressActive(false);
      setProgressWidth(0);
    }, 280);
  }, []);

  useEffect(() => {
    if (isMobile) {
      closeMobile();
    }
  }, [closeMobile, isMobile, location.pathname]);

  const registerPageControls = useCallback((nextControls) => {
    setPageControls((current) => ({
      ...current,
      ...nextControls,
    }));
  }, []);

  const resetPageControls = useCallback(() => {
    setPageControls(defaultPageControls);
  }, []);

  const openOutboundModal = useCallback((prefill = {}) => {
    setOutboundModal({
      open: true,
      phone: prefill.phone || "",
      name: prefill.name || "",
      reason: prefill.reason || "",
      summary: prefill.summary || "",
    });
  }, []);

  const closeOutboundModal = useCallback(() => {
    if (outboundBusy) {
      return;
    }

    setOutboundModal((current) => ({
      ...current,
      open: false,
    }));
  }, [outboundBusy]);

  const runBulkCalls = useCallback(
    async (targets, options = {}) => {
      const confirmMessage =
        options.confirmMessage ||
        `Start calling ${targets.length} patients? Calling AI will call each one sequentially.`;

      if (!targets.length) {
        showToast("No valid targets with phone numbers selected.", true);
        return { success: 0, failed: 0, cancelled: false };
      }

      if (!window.confirm(confirmMessage)) {
        return { success: 0, failed: 0, cancelled: true };
      }

      showToast(
        `Starting bulk calls for ${targets.length} patients...`,
        false,
        5000,
      );

      let success = 0;
      let failed = 0;

      for (const target of targets) {
        try {
          const data = await startOutboundCall({
            phone: target.phone,
            patient_name: target.name,
            reason: target.reason || "follow-up",
          });
          if (data?.status === "failed" || data?.ari?.accepted === false) {
            throw new Error(data?.ari?.message || "ARI did not accept the call");
          }
          success += 1;
        } catch (error) {
          failed += 1;
        }

        await new Promise((resolve) => {
          window.setTimeout(resolve, options.delayMs || 2000);
        });
      }

      showToast(
        `Bulk call complete: ${success} started, ${failed} failed.`,
        failed > 0,
        6000,
      );

      return { success, failed, cancelled: false };
    },
    [showToast],
  );

  const outletContext = useMemo(
    () => ({
      showToast,
      registerPageControls,
      resetPageControls,
      openOutboundModal,
      runBulkCalls,
    }),
    [
      showToast,
      registerPageControls,
      resetPageControls,
      openOutboundModal,
      runBulkCalls,
    ],
  );

  return (
    <>
      <ProgressBar width={progressWidth} active={progressActive} />
      <div className="shell">
        {/* <UtilityBar /> */}
        <div
          className="app-shell-grid"
          style={{
            gridTemplateColumns: `${sidebarWidth}px minmax(0, 1fr)`,
          }}
        >
          <Sidebar
            collapsed={!isMobile && isCollapsed}
            mobile={isMobile}
            mobileOpen={isMobileOpen}
            width={sidebarWidth}
          />
          <div className="app-content-column">
            <Header
              headerDate={formatToday()}
              onToggleSidebar={toggle}
              onOpenOutboundModal={() => openOutboundModal()}
            />
            <main className="app-route-content">
              <ShellContext.Provider value={outletContext}>
                <Outlet context={outletContext} />
              </ShellContext.Provider>
            </main>
            {/* <Footer
              lastUpdated={pageControls.lastUpdated}
              refreshEvery={pageControls.refreshEvery}
              spinning={pageControls.isRefreshing}
              onRefresh={async () => {
                if (!pageControls.onRefresh) {
                  return;
                }

                showProgress();

                try {
                  await pageControls.onRefresh();
                } catch (error) {
                  const normalizedError = normalizeApiError(error);
                  if (!normalizedError.isAbort) {
                    showToast(normalizedError.message, true);
                  }
                } finally {
                  hideProgress();
                }
              }}
            /> */}
          </div>
        </div>
      </div>

      <OutboundCallModal
        open={outboundModal.open}
        form={outboundModal}
        busy={outboundBusy}
        onClose={closeOutboundModal}
        onChange={(field, value) =>
          setOutboundModal((current) => ({
            ...current,
            [field]: value,
          }))
        }
        onSubmit={async (event) => {
          event.preventDefault();

          if (!outboundModal.phone.trim() || !outboundModal.name.trim()) {
            showToast("Phone and name required.", true);
            return;
          }

          setOutboundBusy(true);
          showProgress();

          try {
            const data = await startOutboundCall({
              phone: outboundModal.phone.trim(),
              patient_name: outboundModal.name.trim(),
              reason: outboundModal.reason.trim() || "appointment reminder",
            });

            setOutboundModal((current) => ({
              ...current,
              open: false,
            }));
            const failed = data?.status === "failed" || data?.ari?.accepted === false;
            showToast(
              failed
                ? data?.ari?.message || "Call request was recorded, but ARI did not accept it."
                : `Call start accepted${data?.provider ? ` via ${data.provider}` : ""}${data?.call_id ? ` (${data.call_id})` : ""}`,
              failed,
              failed ? 7000 : 5000,
            );
          } catch (error) {
            const normalizedError = normalizeApiError(error);
            if (!normalizedError.isAbort) {
              showToast(normalizedError.message, true, 7000);
            }
          } finally {
            setOutboundBusy(false);
            hideProgress();
          }
        }}
      />

      <Toast toasts={toasts} onDismiss={removeToast} />
    </>
  );
}
